/* eslint-disable no-underscore-dangle */
import { useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ServiceModal, type ServiceModalState } from '../ServiceModal/ServiceModal'
import { servicesQueryKey } from '../queryKeys'

import {
  Anchor,
  Button,
  Flex,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters
} from '@percent/lemonade'
import { useQueryList } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ErrorView } from '@percent/admin-dashboard/common/components/errorView/ErrorView'
import { PermissionGate } from '@percent/admin-dashboard/common/components/permissionGate/PermissionGate'
import { RowActionsMenu } from '@percent/admin-dashboard/common/components/rowActionsMenu/RowActionsMenu'
import { useServiceListQuery } from './useServiceListQuery'

const headerWithTestId = (column: string, label: string) => <span data-testid={`services-sort-${column}`}>{label}</span>

export function ServiceList() {
  const { t } = useTranslation()
  const { internalServiceService } = useServices()
  const { apiParams, sort, direction, requestSort, searchValue, setSearchValue, submitSearch, clearSearch } =
    useServiceListQuery()
  const [modal, setModal] = useState<ServiceModalState>(null)

  const [{ data, isLoading, isFetching, totalResults, errorMessage, status }, { nextPage, previousPage, refresh }] =
    useQueryList([servicesQueryKey], internalServiceService.getServices, apiParams)

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') submitSearch(searchValue)

    if (event.key === 'Escape') clearSearch()
  }

  const columns = [
    { id: 'name', content: headerWithTestId('name', t('typography.name')), isSortable: true, props: { width: '35%' } },
    {
      id: 'ownerTeam',
      content: headerWithTestId('ownerTeam', t('typography.ownerTeam')),
      isSortable: true,
      props: { width: '20%' }
    },
    { id: 'description', content: t('typography.description'), props: { width: '40%' } },
    { id: 'actions', content: '', props: { width: '5%' } }
  ]

  if (status === 'error' && errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        // Sorting keeps the previous rows while it fetches, so show it loading rather than in the old order
        isLoading={isLoading || isFetching}
        illustration="no-results"
        emptyTableTitle={t('table.internalServices.emptyTable.title')}
        emptyTableDescription={t('table.internalServices.emptyTable.description')}
        handleRequestSort={requestSort}
        sortByColumn={sort}
        sortDirection={direction}
      >
        <TableSearchAndFilters>
          <Flex direction="row" gap="md" align="center" w="100%">
            <SearchInput
              data-testid="services-search-input"
              placeholder={t('table.internalServices.searchPlaceholder')}
              value={searchValue}
              onChange={event => setSearchValue(event.target.value)}
              onKeyDown={handleKeyDown}
              handleClearValue={clearSearch}
              width="100%"
            />
            <PermissionGate permissions="services:can_create">
              <Button
                variant="primary"
                onClick={() => setModal({ kind: 'create' })}
                data-testid="create-service-button"
              >
                {t('button.createService')}
              </Button>
            </PermissionGate>
          </Flex>
        </TableSearchAndFilters>
        <Table>
          <TableBody>
            {data?.map(service => (
              <TableRow key={service.id} data-testid={`service-row-${service.id}`}>
                <TableCell>
                  <Anchor
                    component={Link}
                    to={`/accounts/services/${service.id}`}
                    data-testid={`service-name-${service.id}`}
                  >
                    {service.name}
                  </Anchor>
                </TableCell>
                <TableCell dataTestId={`service-owner-team-${service.id}`}>{service.ownerTeam}</TableCell>
                <TableCell>{service.description}</TableCell>
                <TableCell>
                  <RowActionsMenu
                    label={t('rolesAndPermissions.menu.actions', { name: service.name })}
                    dataTestId={`service-menu-${service.id}`}
                    actions={[
                      {
                        key: 'edit',
                        label: t('button.edit'),
                        onClick: () => setModal({ kind: 'edit', service }),
                        dataTestId: `service-edit-${service.id}`,
                        isVisible: service._permissions.can_edit
                      },
                      {
                        key: 'delete',
                        label: t('button.delete'),
                        onClick: () => setModal({ kind: 'delete', service }),
                        dataTestId: `service-delete-${service.id}`,
                        isVisible: service._permissions.can_delete
                      }
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults ?? 0 })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      <ServiceModal modal={modal} onClose={() => setModal(null)} onChanged={refresh} onDeleted={refresh} />
    </>
  )
}
